import { escapeHtml, fmtDateTime } from "@/lib/api";

// Format ticket de caisse (imprimante thermique 80 mm / 58 mm). Second format d'impression : aucune donnée modifiée.
const CSS = `@page{size:80mm auto;margin:3mm}body{margin:0;padding:0;width:72mm;font-family:"Courier New",Courier,monospace;font-size:12px;color:#000;line-height:1.3}
.c{text-align:center}.b{font-weight:bold}.h{font-size:15px;font-weight:bold}.s{font-size:10.5px}
.hr{border-top:1px dashed #000;margin:5px 0}.row{display:flex;justify-content:space-between;gap:6px}.row span:last-child{white-space:nowrap}
.ln{margin:3px 0}.ln .d{display:flex;justify-content:space-between;gap:6px}.tot{font-size:14px;font-weight:bold}
@media screen{body{padding:8px;margin:auto}}`;

const shell = (title, body) => `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${CSS}</style></head><body>${body}</body></html>`;
const row = (l, v, cls = "") => `<div class="row ${cls}"><span>${escapeHtml(l)}</span><span>${escapeHtml(v)}</span></div>`;
const header = (ctx, docTitle) => `<div class="c h">${escapeHtml(ctx.org)}</div>${ctx.activityName && ctx.activityName !== ctx.org ? `<div class="c s">${escapeHtml(ctx.activityName)}</div>` : ""}${ctx.address ? `<div class="c s">${escapeHtml(ctx.address)}</div>` : ""}${ctx.phone ? `<div class="c s">Tél : ${escapeHtml(ctx.phone)}</div>` : ""}<div class="hr"></div><div class="c b">${escapeHtml(docTitle)}</div>`;
const footer = (txt = "Merci de votre confiance.") => `<div class="hr"></div><div class="c s">${escapeHtml(txt)}</div><div class="c s">Émis le ${escapeHtml(fmtDateTime(new Date().toISOString()))}</div>`;

export function saleTicketHtml(s, ctx) {
  const f = ctx.fmt;
  const lines = (s.lines || []).map((l) => {
    const name = l.description || ((l.product_name || l.name || "") + (l.packaging && l.packaging !== "base" ? ` (${l.packaging})` : ""));
    const d = Number(l.discount || 0);
    const tot = l.total != null ? l.total : (l.unit_price - d) * l.quantity;
    const pct = l.discount_percent != null ? Number(l.discount_percent) : (l.unit_price ? d / l.unit_price * 100 : 0);
    const disc = d > 0 ? `<div class="d s"><span>Remise −${pct.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %${l.negotiated ? " (négocié)" : ""}</span><span>−${escapeHtml(f(d * l.quantity))}</span></div>` : "";
    return `<div class="ln"><div>${escapeHtml(name)}</div><div class="d s"><span>${escapeHtml(ctx.qtyLabel(l))} x ${escapeHtml(f(l.unit_price))}</span><span>${d > 0 ? "" : escapeHtml(f(tot))}</span></div>${disc}${d > 0 ? `<div class="d s"><span>Net ${escapeHtml(f(l.unit_price - d))}</span><span>${escapeHtml(f(tot))}</span></div>` : ""}</div>`;
  }).join("");
  const hasDisc = (s.lines || []).some((l) => Number(l.discount) > 0);
  const gross = s.gross_subtotal != null ? s.gross_subtotal : (s.lines || []).reduce((a, l) => a + l.unit_price * l.quantity, 0);
  const dtot = s.discount_total != null ? s.discount_total : (s.lines || []).reduce((a, l) => a + (l.discount || 0) * l.quantity, 0);
  const body = `${header(ctx, `TICKET ${s.number || ""}`)}
<div class="s">${escapeHtml(fmtDateTime(s.created_at || new Date().toISOString()))}</div>
<div class="s">Client : ${escapeHtml(s.customer_name || "Comptant")}</div>
<div class="s">Vendeur : ${escapeHtml(s.seller_name || "")}</div>
<div class="hr"></div>${lines}<div class="hr"></div>
${hasDisc ? row("Sous-total avant remise", f(gross)) + row("Total des remises", "-" + f(dtot)) : ""}
${s.tax_amount ? row("Total HT", f(s.subtotal_ht != null ? s.subtotal_ht : s.total)) + row(`TVA (${s.tax_rate || 0}%)`, f(s.tax_amount)) : ""}
${row("TOTAL NET", f(s.total), "tot")}
${s.paid != null ? row("Payé", f(s.paid)) : ""}
${s.cash_received != null ? row("Reçu", f(s.cash_received)) : ""}
${s.change_due != null ? row("Monnaie", f(s.change_due)) : ""}
${s.balance ? row("Reste dû", f(s.balance), "b") : ""}
${footer()}`;
  return shell(`Ticket ${s.number || ""}`, body);
}

export function voidTicketHtml(s, ctx) {
  const f = ctx.fmt;
  const body = `${header(ctx, "ANNULATION DE VENTE")}
${row("Vente n°", s.number || "")}
${row("Date vente", fmtDateTime(s.created_at || new Date().toISOString()))}
${row("Client", s.customer_name || "Comptant")}
<div class="hr"></div>
${row("Montant vente", f(s.total))}
${row("Remboursé", f(s.paid || 0), "tot")}
${s.voided_reason ? `<div class="s">Motif : ${escapeHtml(s.voided_reason)}</div>` : ""}
${footer("Cette vente a été annulée.")}`;
  return shell(`Annulation ${s.number || ""}`, body);
}

export function returnTicketHtml(r, ctx) {
  const f = ctx.fmt;
  const lines = (r.lines || []).map((l) => `<div class="ln"><div>${escapeHtml(l.product_name || "")}</div><div class="d s"><span>Qté ${Number(l.quantity).toLocaleString("fr-FR", { maximumFractionDigits: 3 })}</span><span>${escapeHtml(f(l.amount))}</span></div></div>`).join("");
  const body = `${header(ctx, "RETOUR CLIENT")}
${row("Facture", r.sale_number || "")}
${row("Date", fmtDateTime(r.created_at || new Date().toISOString()))}
${row("Client", r.customer_name || "Comptant")}
${row("Solution", ctx.resolution || "")}
<div class="hr"></div>${lines}<div class="hr"></div>
${row("Valeur retour", f(r.refund_amount))}
${row("Remboursé", f(ctx.cash || 0), "tot")}
${ctx.debt > 0 ? row("Dette annulée", f(ctx.debt)) : ""}
${ctx.credit > 0 ? row("Avoir client", f(ctx.credit)) : ""}
<div class="s">Motif : ${escapeHtml(r.reason || "")}</div>
${footer()}`;
  return shell(`Retour ${r.sale_number || ""}`, body);
}

export function reportTicketHtml(p) {
  const kpis = p.kpis.map(([l, v]) => row(l, v)).join("");
  const top = p.top_products.slice(0, 10).map((r) => `<div class="ln"><div>${escapeHtml(r[0])}</div><div class="d s"><span>Qté ${escapeHtml(r[1])}</span><span>${escapeHtml(r[2])}</span></div></div>`).join("") || `<div class="c s">Aucune donnée</div>`;
  const body = `${header({ org: p.org, activityName: p.subtitle }, "RAPPORT DES VENTES")}
<div class="s">Période : ${escapeHtml(p.period)}</div>
<div class="s">PDV : ${escapeHtml(p.location_label)} · Caissier : ${escapeHtml(p.seller_label)}</div>
<div class="hr"></div>${kpis}<div class="hr"></div>
<div class="b">Meilleures ventes</div>${top}
${footer("Biashara-Core · Rapport")}`;
  return shell(`Rapport ${p.org}`, body);
}
